/**
 * SalesDocumentForm — Phase 23 / 25
 *
 * Form for generating customer-facing documents:
 *   חשבונית מס, חשבונית מס קבלה, חשבונית עסקה, חשבונית זיכוי,
 *   תעודת משלוח, תעודת הזמנה, קבלה
 *
 * Key differences from InventoryDocumentForm:
 *   • Shows a Client (לקוח) dropdown instead of Supplier
 *   • Auto-populates unit_price from product.price (selling price, ex-VAT)
 *   • Calls process_inventory_document RPC with client_id in payload
 *   • Warehouse is optional (required only for document types that update inventory)
 *   • On success calls onSuccess(docTypeName, docNumber) so the parent can
 *     swap in the SuccessState panel
 *
 * Phase 25 additions:
 *   • has_line_discount flag on DocumentType (read from DB via parent)
 *   • discount_pct field on each DocLine (string for free typing)
 *   • updateLine recomputes: line_total = qty * unit_price * (1 - discount_pct/100)
 *   • Discount column (%) rendered conditionally when has_line_discount is true
 *   • discount_pct sent in payload lines array
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDocLineSort, sortDocLines } from '@/hooks/useDocLineSort';
import { SortableColHeader } from '@/components/ui/SortableColHeader';
import type { InventoryPrintData } from '@/components/inventory/InventoryDocumentPrintView';
import { Plus, Trash2, Loader as Loader2, CircleAlert as AlertCircle, Calendar, Users, Warehouse as WarehouseIcon, FileText, ChevronDown, Hash, ReceiptText, Percent, Download } from 'lucide-react';
import DocumentPickerModal, { type ImportableLine } from '@/components/modals/DocumentPickerModal';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { supabase } from '@/lib/supabase';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Client {
  id:           string;
  name:         string;
  is_vat_liable:boolean;
}

interface Warehouse {
  id:   string;
  name: string;
}

interface Product {
  id:    string;
  sku:   string;
  name:  string;
  cost:  number | null;
  price: number;
}

interface DocumentType {
  id:                string;
  name:              string;
  next_number:       number;
  prefix:            string;
  requires_receipt:  boolean;
  is_vat_liable:     boolean;
  updates_inventory: boolean;
  has_line_discount: boolean;   // Phase 25
}

interface DocLine {
  product_id:   string;
  quantity:     string;     // free-type string
  unit_price:   string;     // free-type string
  discount_pct: string;     // Phase 25 — "0" … "100"
  line_total:   number;     // computed
}

const EMPTY_LINE: DocLine = {
  product_id:   '',
  quantity:     '1',
  unit_price:   '0',
  discount_pct: '0',
  line_total:   0,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);

function parseNum(s: string): number {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

function fmtMoney(n: number): string {
  return n.toFixed(2);
}

function fmtCurrency(n: number): string {
  return `₪${n.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Compute line total respecting optional discount */
function computeLineTotal(quantity: string, unit_price: string, discount_pct: string): number {
  const qty  = parseNum(quantity);
  const price = parseNum(unit_price);
  const disc  = Math.min(Math.max(parseNum(discount_pct), 0), 100); // clamp 0-100
  return qty * price * (1 - disc / 100);
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  docType:               DocumentType;
  companyId:             string;
  vatRate:               number;         // company default VAT rate
  showToast:             (msg: string, ok?: boolean) => void;
  onSuccess:             (docTypeName: string, docRef: string, printData: InventoryPrintData) => void;
  onBeforeFirstSubmit?:  () => Promise<boolean>;
  initialLines?:         ImportableLine[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SalesDocumentForm({
  docType,
  companyId,
  vatRate,
  showToast,
  onSuccess,
  onBeforeFirstSubmit,
  initialLines,
}: Props) {

  const showDiscount = docType.has_line_discount;
  const [showDocPicker, setShowDocPicker] = useState(false);

  // ── Reference data ────────────────────────────────────────────────────────
  const [clients,    setClients]    = useState<Client[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products,   setProducts]   = useState<Product[]>([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError,   setRefError]   = useState<string | null>(null);

  const fetchRefData = useCallback(async () => {
    if (!companyId) return;
    setRefLoading(true); setRefError(null);
    try {
      const [cliRes, whRes, prodRes] = await Promise.all([
        supabase
          .from('inventory_clients')
          .select('id, name, is_vat_liable')
          .eq('company_id', companyId)
          .order('name'),
        supabase
          .from('inventory_warehouses')
          .select('id, name')
          .eq('company_id', companyId)
          .eq('is_external', false)
          .order('name'),
        supabase
          .from('inventory_products')
          .select('id, sku, name, cost, price')
          .eq('company_id', companyId)
          .order('name'),
      ]);
      if (cliRes.error)  throw new Error(`לקוחות: ${cliRes.error.message}`);
      if (whRes.error)   throw new Error(`מחסנים: ${whRes.error.message}`);
      if (prodRes.error) throw new Error(`מוצרים: ${prodRes.error.message}`);
      setClients(   (cliRes.data  ?? []) as Client[]);
      setWarehouses((whRes.data   ?? []) as Warehouse[]);
      setProducts(  (prodRes.data ?? []) as Product[]);
    } catch (e) {
      setRefError(e instanceof Error ? e.message : 'שגיאה בטעינת נתונים');
    } finally {
      setRefLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchRefData(); }, [fetchRefData]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return;
      supabase.from('profiles').select('full_name').eq('id', user.id).maybeSingle()
        .then(({ data }) => { if (data?.full_name) setIssuedByName(data.full_name); });
    });
  }, []);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [issueDate,   setIssueDate]   = useState<string>(today());
  const [clientId,    setClientId]    = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [notes,       setNotes]       = useState<string>('');
  const [lines,       setLines]       = useState<DocLine[]>(() => {
    if (initialLines?.length) {
      return initialLines.map(l => ({
        product_id:   l.product_id,
        quantity:     String(l.quantity),
        unit_price:   fmtMoney(l.unit_price),
        discount_pct: String(l.discount_pct ?? 0),
        line_total:   l.line_total,
      }));
    }
    return [{ ...EMPTY_LINE }];
  });
  const [formError,   setFormError]   = useState<string | null>(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [issuedByName, setIssuedByName] = useState('');
  const lineSort = useDocLineSort();

  const handleSortLines = useCallback((key: string) => {
    const k = key as import('@/hooks/useDocLineSort').DocLineSortKey;
    const nextDir = lineSort.sort?.key === k && lineSort.sort.dir === 'asc' ? 'desc' as const : 'asc' as const;
    lineSort.toggle(k);
    setLines(prev => sortDocLines(prev, { key: k, dir: nextDir }, products));
  }, [lineSort, products]);

  // ── Effective VAT: use client setting if available, else doc type flag ──────
  const selectedClient = useMemo(
    () => clients.find(c => c.id === clientId) ?? null,
    [clients, clientId]
  );

  /**
   * VAT applies when:
   *   1. The document type is vat-liable (is_vat_liable), AND
   *   2. The selected client is vat-liable (or no client selected — default liable)
   */
  const isVatLiable = docType.is_vat_liable &&
    (selectedClient === null || selectedClient.is_vat_liable);

  const effectiveVatRate = isVatLiable ? vatRate : 0;

  // ── Product lookup map ────────────────────────────────────────────────────
  const productMap = useMemo(() => {
    const m: Record<string, Product> = {};
    for (const p of products) m[p.id] = p;
    return m;
  }, [products]);

  // ── Line helpers ──────────────────────────────────────────────────────────
  const updateLine = (idx: number, patch: Partial<DocLine>) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const merged = { ...l, ...patch };
      merged.line_total = computeLineTotal(
        merged.quantity,
        merged.unit_price,
        merged.discount_pct,
      );
      return merged;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (idx: number) =>
    setLines(prev => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));

  /** Auto-fill unit_price from product selling price (sales documents always use price) */
  const handleProductSelect = (idx: number, productId: string) => {
    const prod = productMap[productId];
    const defaultPrice = prod ? prod.price : 0;   // selling price (ex-VAT)
    updateLine(idx, { product_id: productId, unit_price: fmtMoney(defaultPrice) });
  };

  // ── Totals ────────────────────────────────────────────────────────────────
  const subtotal  = useMemo(() => lines.reduce((s, l) => s + l.line_total, 0), [lines]);
  const vatAmount = subtotal * (effectiveVatRate / 100);
  const total     = subtotal + vatAmount;

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (docType.updates_inventory && !warehouseId) return 'מסמך זה מעדכן מלאי — יש לבחור מחסן';
    if (lines.length === 0) return 'יש להוסיף לפחות שורה אחת';
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!;
      if (!l.product_id) return `שורה ${i + 1}: יש לבחור מוצר`;
      if (parseNum(l.quantity) <= 0) return `שורה ${i + 1}: כמות חייבת להיות גדולה מאפס`;
      const disc = parseNum(l.discount_pct);
      if (disc < 0 || disc > 100) return `שורה ${i + 1}: הנחה חייבת להיות בין 0 ל-100`;
    }
    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) { setFormError(err); return; }
    // Phase 80: intercept before first document submission
    if (onBeforeFirstSubmit) {
      const proceed = await onBeforeFirstSubmit();
      if (!proceed) return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const payload = {
        company_id:       companyId,
        document_type_id: docType.id,
        warehouse_id:     warehouseId || null,
        supplier_id:      null,
        client_id:        clientId   || null,
        issued_by_name:   issuedByName || null,
        issue_date:       issueDate,
        subtotal,
        vat_amount:       vatAmount,
        total,
        notes:            notes.trim() || null,
        lines: lines.map(l => ({
          product_id:   l.product_id,
          quantity:     parseNum(l.quantity),
          unit_price:   parseNum(l.unit_price),
          discount_pct: parseNum(l.discount_pct),
          line_total:   l.line_total,
        })),
      };

      const { data, error: rpcErr } = await supabase.rpc(
        'process_inventory_document',
        { payload },
      );

      if (rpcErr) throw rpcErr;

      const documentId = typeof data === 'string' ? data : undefined;

      const generatedNumber = docType.next_number;
      const docRef = `${docType.prefix || ''}${generatedNumber}`;
      showToast(`${docType.name} ${docRef} הופק בהצלחה`, true);

      // Build print data from in-scope form state
      const printData: InventoryPrintData = {
        docTypeName:  docType.name,
        docNumber:    docRef,
        date:         issueDate,
        clientName:   selectedClient?.name ?? undefined,
        lines: lines.map(l => ({
          product_id:   l.product_id,
          product_name: productMap[l.product_id]?.name ?? l.product_id,
          quantity:     parseNum(l.quantity),
          unit_price:   parseNum(l.unit_price),
          discount_pct: parseNum(l.discount_pct),
          line_total:   l.line_total,
        })),
        subtotal,
        vatAmount,
        grandTotal:  total,
        vatRate:     effectiveVatRate,
        documentId,
        clientId:    clientId || undefined,
        issuedByName: issuedByName || undefined,
      };
      onSuccess(docType.name, docRef, printData);

    } catch (e) {
      const msg = e instanceof Error ? e.message
        : (typeof e === 'object' && e !== null && 'message' in e)
          ? String((e as { message: unknown }).message)
          : String(e);
      setFormError(msg);
      showToast(msg, false);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / error guards ────────────────────────────────────────────────
  if (refLoading) return (
    <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
      <Loader2 className="w-5 h-5 animate-spin" />
      <span className="text-sm">טוען נתונים…</span>
    </div>
  );

  if (refError) return (
    <div className="flex items-center gap-3 m-6 px-4 py-3 bg-red-50 border border-red-200
                    rounded-xl text-sm text-red-700">
      <AlertCircle className="w-4 h-4 shrink-0" />{refError}
    </div>
  );

  // ── Grid column template ──────────────────────────────────────────────────
  // With discount:    product | qty | unit_price | discount | line_total | remove
  // Without discount: product | qty | unit_price | line_total | remove
  const gridCols = showDiscount
    ? 'grid-cols-[1fr_7rem_8rem_6rem_8rem_2.5rem]'
    : 'grid-cols-[1fr_7rem_8rem_8rem_2.5rem]';

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5" dir="rtl">

      {/* ── Section header ── */}
      <div className="flex items-center justify-between px-6 py-4
                      bg-blue-50 border-b border-blue-200 rounded-t-2xl">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-xl shadow-sm">
            <ReceiptText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-blue-800">{docType.name}</h2>
            <p className="text-xs text-blue-500">מסמך לקוח — הזן פרטים להפקה</p>
          </div>
        </div>

        {/* Next number badge: PREFIX + number */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg
                        border border-blue-200 shadow-sm">
          <Hash className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-500">מספר הבא:</span>
          <span className="font-mono font-bold text-orange-600 text-sm tracking-wide">
            {(docType.prefix || '') + docType.next_number}
          </span>
        </div>
      </div>

      {/* ── Form body ── */}
      <div className="px-6 space-y-6 pb-6">

        {/* Form error */}
        {formError && (
          <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200
                          rounded-xl text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{formError}</span>
          </div>
        )}

        {/* ── Header fields ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          {/* Issue date */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide
                               flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              תאריך הפקה
            </label>
            <input
              type="date"
              value={issueDate}
              onChange={e => setIssueDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-blue-400
                         focus:border-transparent"
              dir="ltr"
            />
          </div>

          {/* Client */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide
                               flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" />
              לקוח
              <span className="text-gray-400 normal-case font-normal">(אופציונלי)</span>
            </label>
            <SearchableSelect
              options={clients.map(c => ({ value: c.id, label: c.name }))}
              value={clientId}
              onChange={setClientId}
              placeholder="— בחר לקוח —"
            />
            {clients.length === 0 && (
              <p className="text-xs text-amber-600">אין לקוחות — הוסף לקוחות בהגדרות המלאי</p>
            )}
            {/* VAT exempt notice */}
            {selectedClient && !selectedClient.is_vat_liable && (
              <p className="text-xs text-blue-600 flex items-center gap-1">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400" />
                לקוח פטור ממע&quot;מ — המסמך יופק ללא מע&quot;מ
              </p>
            )}
          </div>

          {/* Warehouse — required only if doc updates inventory */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide
                               flex items-center gap-1.5">
              <WarehouseIcon className="w-3.5 h-3.5" />
              מחסן
              {docType.updates_inventory
                ? <span className="text-red-500">*</span>
                : <span className="text-gray-400 normal-case font-normal">(אופציונלי)</span>}
            </label>
            <div className="relative">
              <select
                value={warehouseId}
                onChange={e => setWarehouseId(e.target.value)}
                className="w-full appearance-none px-3 py-2.5 border border-gray-300 rounded-lg
                           text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400
                           focus:border-transparent pl-8"
              >
                <option value="">— בחר מחסן —</option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4
                                      text-gray-400 pointer-events-none" />
            </div>
            {warehouses.length === 0 && (
              <p className="text-xs text-amber-600">אין מחסנים — הוסף מחסנים בהגדרות המלאי</p>
            )}
          </div>
        </div>

        {/* ── Lines table ── */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide
                           flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" />
              פריטים
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowDocPicker(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100
                           rounded-lg transition-colors border border-orange-200"
              >
                <Download className="w-3.5 h-3.5" />
                ייבא ממסמך
              </button>
              <button
                type="button"
                onClick={addLine}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                           text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100
                           rounded-lg transition-colors border border-blue-200"
              >
                <Plus className="w-3.5 h-3.5" />
                הוסף שורה
              </button>
            </div>
          </div>

          {showDocPicker && (
            <DocumentPickerModal
              companyId={companyId}
              onClose={() => setShowDocPicker(false)}
              onSelect={(imported) => {
                const newLines: DocLine[] = imported.map(l => ({
                  product_id:   l.product_id,
                  quantity:     String(l.quantity),
                  unit_price:   fmtMoney(l.unit_price),
                  discount_pct: String(l.discount_pct ?? 0),
                  line_total:   l.line_total,
                }));
                setLines(prev => {
                  const hasContent = prev.some(l => l.product_id !== '');
                  return hasContent ? [...prev, ...newLines] : newLines;
                });
                setShowDocPicker(false);
              }}
            />
          )}

          {/* Column headers */}
          <div className={`grid ${gridCols} gap-2 px-3
                          text-xs font-semibold text-gray-500 uppercase tracking-wide`}>
            <SortableColHeader label="מוצר" sortKey="product_name" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} />
            <SortableColHeader label="כמות" sortKey="quantity" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} className="justify-center" />
            <SortableColHeader label="מחיר יח׳ (ללא מע״מ)" sortKey="unit_price" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} className="justify-center" />
            {showDiscount && (
              <SortableColHeader label="הנחה" sortKey="discount_pct" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} className="justify-center">
                <Percent className="w-3 h-3" />
              </SortableColHeader>
            )}
            <SortableColHeader label="סה״כ שורה" sortKey="line_total" activeKey={lineSort.sort?.key ?? null} dir={lineSort.sort?.dir ?? null} onSort={handleSortLines} />
            <span />
          </div>

          {/* Lines */}
          <div className="space-y-2">
            {lines.map((line, idx) => (
              <div
                key={idx}
                className={`grid ${gridCols} gap-2 items-center
                           bg-gray-50/70 px-3 py-2.5 rounded-xl border border-gray-100`}
              >
                {/* Product */}
                <SearchableSelect
                  options={products.map(p => ({ value: p.id, label: p.name, secondary: p.sku || undefined }))}
                  value={line.product_id}
                  onChange={v => handleProductSelect(idx, v)}
                  placeholder="— בחר מוצר —"
                  compact
                />

                {/* Quantity */}
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={line.quantity}
                  onChange={e => updateLine(idx, { quantity: e.target.value })}
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm
                             text-center font-mono focus:outline-none focus:ring-2
                             focus:ring-blue-400 focus:border-transparent"
                  dir="ltr"
                />

                {/* Unit price */}
                <div className="relative">
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2
                                   text-gray-400 text-xs pointer-events-none">₪</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.unit_price}
                    onChange={e => updateLine(idx, { unit_price: e.target.value })}
                    className="w-full pr-7 pl-2.5 py-2 border border-gray-200 rounded-lg
                               text-sm font-mono focus:outline-none focus:ring-2
                               focus:ring-blue-400 focus:border-transparent"
                    dir="ltr"
                  />
                </div>

                {/* Discount % — conditional */}
                {showDiscount && (
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={line.discount_pct}
                      onChange={e => updateLine(idx, { discount_pct: e.target.value })}
                      className="w-full pl-6 pr-2.5 py-2 border border-amber-200 rounded-lg
                                 text-sm font-mono text-center focus:outline-none focus:ring-2
                                 focus:ring-amber-400 focus:border-transparent bg-amber-50/40"
                      dir="ltr"
                      placeholder="0"
                    />
                    <Percent className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3
                                        text-amber-400 pointer-events-none" />
                  </div>
                )}

                {/* Line total */}
                <div className="px-2.5 py-2 bg-white border border-gray-100 rounded-lg
                                text-sm font-mono font-semibold text-gray-700 text-left tabular-nums"
                     dir="ltr">
                  {fmtCurrency(line.line_total)}
                </div>

                {/* Remove */}
                <button
                  type="button"
                  onClick={() => removeLine(idx)}
                  disabled={lines.length === 1}
                  className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50
                             rounded-lg transition-colors disabled:opacity-30
                             disabled:cursor-not-allowed"
                  title="הסר שורה"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Notes ── */}
        <div className="space-y-1.5">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            הערות
            <span className="normal-case font-normal text-gray-400 mr-1">(אופציונלי)</span>
          </label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="הערות למסמך…"
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm resize-none
                       focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          />
        </div>

        {/* ── Totals + Submit ── */}
        <div className="flex items-end justify-between gap-6 pt-3 border-t border-gray-100">

          {/* Submit */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm
                       font-semibold bg-blue-600 hover:bg-blue-700 transition-colors
                       shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" />מעבד…</>
              : <><ReceiptText className="w-4 h-4" />הפק מסמך</>
            }
          </button>

          {/* Totals breakdown */}
          <div className="space-y-1.5 text-sm min-w-[230px]" dir="ltr">
            <div className="flex items-center justify-between gap-8 text-gray-600">
              <span className="text-right">סה״כ לפני מע״מ</span>
              <span className="font-mono font-semibold">{fmtCurrency(subtotal)}</span>
            </div>
            <div className="flex items-center justify-between gap-8 text-gray-500 text-xs">
              <span className="text-right">
                מע״מ {isVatLiable ? `${effectiveVatRate}%` : '(פטור)'}
              </span>
              <span className="font-mono">{fmtCurrency(vatAmount)}</span>
            </div>
            <div className="flex items-center justify-between gap-8 pt-1.5 border-t
                            border-gray-200 font-bold text-gray-900">
              <span className="text-right">סה״כ לתשלום</span>
              <span className="font-mono text-base">{fmtCurrency(total)}</span>
            </div>
          </div>
        </div>

      </div>{/* end form body */}
    </div>
  );
}
